import { Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from 'app/app.service';
import { Empresa } from 'app/interfaces/empresa';
import { Produto } from 'app/interfaces/enum/produto.enum';
import { Grupo } from 'app/interfaces/grupo';
import { JasperRelatorioBusca } from 'app/interfaces/jasper/jasper-relatorio-busca';
import { Role } from 'app/interfaces/role';
import { Usuario } from 'app/interfaces/usuario';
import { FileUploaderDropzoneComponent } from 'app/layout/components/file-uploader-dropzone/file-uploader-dropzone.component';
import { EmpresaHttpService } from 'app/main/empresa/empresa-http.service';
import { GrupoHttpService } from 'app/main/grupo/grupo-http.service';
import { JasperHttpService } from 'app/main/relatorio/jasper-http.service';
import { RoleHttpService } from 'app/main/role/role-http.service';
import { EmailHttpService } from 'app/main/shared/services/email-http.service';
import { UsuarioHttpService } from 'app/main/usuario/usuario-http.service';
import handleErrorMessage from 'app/utils/error-message-handler';
import { SessionStorageService } from 'app/utils/session-storage.service';
import { MessageService } from 'app/utils/toastr-custom/message.service';
import { debounceTime, distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { UsuarioAddService } from './usuario-add.service';
import { ParametroUtilsService } from 'app/utils/parametro-utils.service';
import { ParceiroNegocio } from 'app/interfaces/parceiro-negocio';

@Component({
  selector: 'app-usuario-add',
  templateUrl: './usuario-add.component.html',
  styleUrls: ['./usuario-add.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UsuarioAddComponent implements OnInit, OnDestroy {

  usuario: Usuario;
  form: FormGroup;
  subscription: Subscription;
  submitted = false;
  grupos = [] as Grupo[];
  empresas = [] as Empresa[];
  usuarioLogado: Usuario;
  roles = [] as Role[];
  rolesSelecionadas = [] as Role[];
  roleSubscription: Subscription;
  loadingRoles = true;
  produtos = Produto;
  ocultarProduto = false;
  tinymceConfig: any;
  parceiroNegocioId: number; //utilizado caso seja criado a partir do parceiro de negócio

  // subscription
  _unsubscribeAll = new Subject<any>();

  jasperReportText = new Subject<string>();
  jasperRelatorioBusca = [] as JasperRelatorioBusca[];

  constructor(
    private _appService: AppService,
    private _router: Router,
    private _empresaHttpService: EmpresaHttpService,
    private _grupoHttpService: GrupoHttpService,
    private _usuarioHttpService: UsuarioHttpService,
    private _messageService: MessageService,
    private _sessionStorageService: SessionStorageService,
    private _emailHttpService: EmailHttpService,
    private _roleHttpService: RoleHttpService,
    private _jasperHttpService: JasperHttpService,
    private _activatedRoute: ActivatedRoute,
    private _usuarioAddService: UsuarioAddService,
    private _parametroUtilsService: ParametroUtilsService
  ) {
    this.usuarioLogado = JSON.parse(this._sessionStorageService.getItem("user"));
  }

  ngOnDestroy(): void {
    this.subscription ? this.subscription.unsubscribe() : null;
    this.roleSubscription ? this.roleSubscription.unsubscribe() : null;
  }

  ngOnInit(): void {
    this.ocultarProduto = this._parametroUtilsService.isSomenteRodoviario;
    this.initForm();
    this.getUsuarioDeParceiroDeNegocio();
    this.getEmpresas();
    this.getGrupos();
    this.getRoles();
    this.subscribeFiltros();
    this.tinymceConfig = this.getTinyMceConfig();
  }

  subscribeFiltros() {

    this.jasperReportText.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this._unsubscribeAll),
    )
      .subscribe(value => {
        if (value?.length >= 3) {
          this.findJasperByNome(value);
        }
      });
  }

  getEmpresas() {
    this._empresaHttpService.getEmpresas().subscribe({
      next: (result: Empresa[]) => {
        this.empresas = result;
      },
      error: (error) => {
        console.log(error);
        handleErrorMessage(this._messageService, error)
      }
    })
  }

  getRoles() {
    this._roleHttpService.getAllAux().subscribe({
      next: (result: Role[]) => {
        this.roles = result.filter(r => r.nome !== 'MASTER');
      },
      error: (error) => {
        console.log(error);
        handleErrorMessage(this._messageService, error)
      }
    }).add(() => {
      this.loadingRoles = false;
    })
  }

  getGrupos() {
    this._grupoHttpService.getGrupos().subscribe({
      next: (result: Grupo[]) => {
        this.grupos = result;
      },
      error: (error) => {
        console.log(error);
        handleErrorMessage(this._messageService, error)
      }
    })
  }

  /**
   * Função para iniciar o formulário de cadastro;
   */
  initForm() {
    this.form = new FormGroup({
      usuId: new FormControl(''),
      nome: new FormControl('', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(200)])),
      ativo: new FormControl(true),
      usuarioGestorVisualizacao: new FormControl(false),
      visualizaGrupoMesmoNivel: new FormControl(false),
      produtos: new FormControl(this.ocultarProduto ? [Produto.TR] : null, Validators.compose([Validators.required])),
      email: new FormControl('', Validators.compose([Validators.required, Validators.email, Validators.minLength(3), Validators.maxLength(100)])),
      emailSenha: new FormControl('', Validators.compose([Validators.required])),
      grupo: new FormGroup({
        grpId: new FormControl('', Validators.compose([Validators.required])),
        nome: new FormControl('')
      }),
      empresa: new FormGroup({
        empId: new FormControl('', Validators.compose([Validators.required])),
        nome: new FormControl(''),
      }),
      tipoAssinatura: new FormControl(null),
      assinaturaEmailImagem: new FormControl(null),
      assinaturaEmailTexto: new FormControl(null),
      permissoesJasper: new FormControl([]),
    });
  }

  getUsuarioDeParceiroDeNegocio() {
    this._activatedRoute.data.subscribe(result => {
      if (result.parceiroNegocio) {
        this.parceiroNegocioId = result.parceiroNegocio.parceiroNegocioId;
        this.usuario = this._usuarioAddService.construirObjetoDoForm(result.parceiroNegocio);

        this.form.patchValue(this.usuario);

        if (this.ocultarProduto && !this.form.controls.produtos.value?.length) {
          this.form.controls.produtos.setValue([Produto.TR]);
        }
      }
    });
  }


  /**
   * 
   * @param obj1 
   * @param obj2 
   * @returns 
   */
  compareEmpresa(obj1: any, obj2: any) {
    let b: boolean;
    b = obj1 && obj2 ? (obj1.empId === obj2.empId) : obj1 === obj2;
    return b;
  }

  compareGrupo(obj1: any, obj2: any) {
    let b: boolean;
    b = obj1 && obj2 ? (obj1.grpId === obj2.grpId) : obj1 === obj2;
    return b;
  }

  /**
   * Função para gravar novo registro;
   */
  async salvar() {

    // seta o formulario como submitted;
    this.submitted = true;

    // verifica se todas informações foram preenchidas corretamente;
    if (this.form.valid && this.rolesSelecionadas.length > 0) {

      // pergunta se quer realmente salvar;
      let confirmed = await this._appService.fireSwalAsync('Atenção!', 'Deseja realmente gravar um novo registro?', 'Salvar');

      if (confirmed) {

        // mostra o progress bar;
        this._appService.progressSpinnerDlg = true;

        try {

          // pega os valores do form;
          this.usuario = this.form.value;
          this.usuario.roles = this.rolesSelecionadas;
          this.usuario.assinaturaEmailTexto = this.hasAssinaturaEmailTexto ? this.assinaturaEmailTextoTratado() : undefined;

          if (this.form.controls['tipoAssinatura'].value === 'IMAGEM') {
            this.usuario.assinaturaEmailTexto = undefined;
          } else if (this.form.controls['tipoAssinatura'].value === 'TEXTO') {
            this.usuario.assinaturaEmailImagem = undefined;
          }

          // chama api para salvar o usuário;
          this.usuario = await this._usuarioHttpService.salvar(this.usuario, this.parceiroNegocioId).toPromise();

          // se tudo deu certo, mostra mensagem de success;
          this._messageService.success('Atenção', 'Registro salvo com sucesso.');
          this.criaUsuarioSenha(this.usuario.email);

          if (this.parceiroNegocioId) {
            this._router.navigate(['/parceiro/parceiro-negocio/details', this.parceiroNegocioId]);
          } else {
            this._router.navigateByUrl('/usuario');
          }

        } catch (error) {
          console.info(error)
          handleErrorMessage(this._messageService, error);
        }

        // esconde o progress bar;
        this._appService.progressSpinnerDlg = false;

      }

    } else {
      this._messageService.warning('Atenção', 'Preencha todos os campos.');
    }
  }

  criaUsuarioSenha(email: string) {
    this._emailHttpService.resetSenha(email).subscribe({
      next: (result) => {
      }
    })
  }

  /**
   * Voltar para tela anterior;
   */
  async voltar() {
    if (this.form.dirty) {
      let confirmed = await this._appService.fireSwalAsync("Deseja Realmente voltar?", "Você perderá as alterações feitas até agora.", 'Voltar');
      if (confirmed) {
        this._router.navigateByUrl('/usuario');
      }
    } else {
      this._router.navigateByUrl('/usuario');
    }
  }

  /**
 * Função para marcar todas roles;
 */
  selecionarTodasRoles() {
    this.rolesSelecionadas = this.roles.filter(x => x.rolId);
  }

  /**
   * Função para desmarcar todas roles;
   */
  desmarcarTodasRoles() {
    this.rolesSelecionadas = [];
  }

  /**
  * 
  * @param nome 
  */
  findJasperByNome(nome: string) {
    this._jasperHttpService.findByNome(nome).subscribe({
      next: (result) => {
        this.jasperRelatorioBusca = result?.resourceLookup;
      },
      error: (err) => {
        console.log(err);
        handleErrorMessage(this._messageService, err);
      }
    })
  }

  isAssinaturaValida(file: File): boolean {
    const tamanhoPermitidoKb = 300;
    const assinaturaTipo = 'PNG';
    const tamanhoArquivoKb = file.size / 1000;
    
    if (tamanhoArquivoKb >= tamanhoPermitidoKb) {
      this._messageService.warning("Atenção", `Assinatura com tamanho inválido! (limite ${tamanhoPermitidoKb}Kb)`);
      return false;
    } else if (!file.type.toUpperCase().endsWith(assinaturaTipo)) {
      this._messageService.warning("Atenção", `Tipo de imagem inválido! (tipo aceito: ${assinaturaTipo})`);
      return false;
    } else {
      return true; 
    }
  }

  onUpload(componentUpload: FileUploaderDropzoneComponent, arquivo: File) {
    componentUpload.inicioUpload();

    const img = new Image();
    img.src = window.URL.createObjectURL(arquivo);

    img.onload = () => {
      const maxWidth = 1475;
      const maxHeight = 457;

      if (img.naturalHeight > maxHeight || img.naturalWidth > maxWidth) {
        this._messageService.warning("Atenção", `Assinatura de e-mail com dimensões inválidas! (permitido: ${maxWidth} x ${maxHeight})`);
      } else {
        let reader = new FileReader();
        reader.readAsDataURL(arquivo);
        reader.onload = () => {
          this.form.controls['assinaturaEmailImagem'].setValue((reader.result as string));
        }
      }

      componentUpload.arquivoSalvo();
    }
  }

  removerAssinatura() {
    if (this.form.controls['tipoAssinatura'].value === 'IMAGEM') {
      this.form.controls['assinaturaEmailImagem'].setValue(undefined);
    } else if (this.form.controls['tipoAssinatura'].value === 'TEXTO') {
      this.form.controls['assinaturaEmailTexto'].setValue(undefined);
    }
  }

  assinaturaEmailTextoTratado() {
    return new DOMParser().parseFromString(this.form.controls['assinaturaEmailTexto'].value, 'text/html').body.innerHTML;
  }

  get hasAssinaturaEmailTexto() {
    if (this.form.controls['assinaturaEmailTexto'].value) {
      const texto = new DOMParser().parseFromString(this.form.controls['assinaturaEmailTexto'].value, 'text/html').body.textContent.trim().replaceAll('\n', '');
      if (texto) {
        return true;
      }
    }

    return false;
  }

  getTinyMceConfig() {
    return {
      height: 220,
      max_height: 220,
      min_height: 220,
      menubar: false,
      statusbar: false,
      plugins: 'fullpage',
      toolbar:
        'bold italic strikethrough forecolor backcolor | link | alignleft aligncenter alignright alignjustify  | numlist bullist outdent indent  | removeformat',
      image_advtab: true,
      imagetools_toolbar:
        'rotateleft rotateright | flipv fliph | editimage imageoptions',
      content_css: [
        '//fonts.googleapis.com/css?family=Montserrat:300,300i,400,400i',
        '//www.tinymce.com/css/codepen.min.css',
      ],
      content_style: 'body { font-family: "Montserrat", Helvetica, Arial, serif; font-size: 12px; color: #6e6b7b}'
    };
  }
}
